import type { InvoiceLineItem } from "@/models/invoice.model";
import type { InvoiceListJoin } from "@/repositories/invoice-records";
import { format } from "date-fns";
import { getInvoiceSubject } from "@/lib/invoices/invoice-subject";

export type QuickBooksInvoice = InvoiceListJoin & { line_items?: InvoiceLineItem[] };

/**
 * Formats invoice number as SHY-{invoice_number}-{MM}{DD}{YYYY}-{HH}{MM}
 */
function formatInvoiceNumber(invoiceNumber: number, issueDate: Date | null): string {
  if (!issueDate) {
    return `SHY-${invoiceNumber}`;
  }
  const date = new Date(issueDate);
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, "0");
  const MM = String(date.getMinutes()).padStart(2, "0");
  return `SHY-${invoiceNumber}-${mm}${dd}-${hh}${MM}`;
}

function getGroupKey(invoice: QuickBooksInvoice): string {
  const subject = getInvoiceSubject(invoice);
  const propertyId = subject.propertyId || "";
  const unitNumber = subject.unitNumber || "";
  return `${propertyId}-${unitNumber}`;
}

function compareInvoicesBySubject(a: QuickBooksInvoice, b: QuickBooksInvoice): number {
  const subjectA = getInvoiceSubject(a);
  const subjectB = getInvoiceSubject(b);
  const propertyCompare = (subjectA.propertyName || "").localeCompare(subjectB.propertyName || "");
  if (propertyCompare !== 0) return propertyCompare;

  const unitCompare = (subjectA.unitNumber || "").localeCompare(subjectB.unitNumber || "", undefined, {
    numeric: true,
    sensitivity: "base",
  });
  return unitCompare || a.invoice_number - b.invoice_number;
}

function quickBooksLineItems(invoice: QuickBooksInvoice) {
  const omittedParticulars = getInvoiceSubject(invoice).quickBooks.omitZeroPricedLineItems;
  return (invoice.line_items ?? []).filter((item) => {
    const zeroPriced = (item.total_price ?? 0) === 0 && (item.unit_price ?? 0) === 0;
    return !zeroPriced || !omittedParticulars.includes(item.particular);
  });
}

function formatQuickBooksServiceDate(value: string | null): string {
  if (!value) return "";
  const localDateValue = /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value;
  return format(new Date(localDateValue), "MM/dd/yyyy");
}

/**
 * Escapes a field for CSV format
 * Wraps in quotes if field contains commas, quotes, or newlines
 */
function escapeCSVField(field: string | number): string {
  const fieldStr = String(field);
  // If field contains comma, quote, or newline, wrap in quotes and escape quotes
  if (fieldStr.includes(",") || fieldStr.includes('"') || fieldStr.includes("\n")) {
    return `"${fieldStr.replace(/"/g, '""')}"`;
  }
  return fieldStr;
}

export function generateQuickBooksCSV(invoices: QuickBooksInvoice[]): string {
  // QuickBooks CSV header
  const header = [
    "*InvoiceNo",
    "*Customer",
    "*InvoiceDate",
    "*DueDate",
    "Terms",
    "Location",
    "Memo",
    "Item(Product/Service)",
    "ItemDescription",
    "ItemQuantity",
    "ItemRate",
    "*ItemAmount",
    "Service Date",
  ];

  const rows: string[][] = [header];

  // First, sort all invoices by property name, then by unit number
  // This ensures proper grouping even if invoices have different dates
  const sortedInvoices = [...invoices].sort(compareInvoicesBySubject);

  // Group invoices by property + unit (already sorted)
  const groups = new Map<string, QuickBooksInvoice[]>();
  for (const invoice of sortedInvoices) {
    const key = getGroupKey(invoice);
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(invoice);
  }

  // Process each group
  for (const groupInvoices of groups.values()) {
    // Invoices are already sorted within groups, but ensure invoice_number order within each group
    groupInvoices.sort((a, b) => a.invoice_number - b.invoice_number);

    // Get the first invoice in the group for header information
    const firstInvoice = groupInvoices[0];
    const firstSubject = getInvoiceSubject(firstInvoice);
    const propertyName = firstSubject.propertyName || "Unknown Property";
    const unitNumber = firstSubject.unitNumber || "";
    const invoiceDate = firstInvoice.issue_date ? format(new Date(firstInvoice.issue_date), "MM/dd/yyyy") : "";
    const dueDate = firstInvoice.due_date ? format(new Date(firstInvoice.due_date), "MM/dd/yyyy") : "";
    const serviceDate = firstInvoice.issue_date ? format(new Date(firstInvoice.issue_date), "MM/dd/yyyy") : "";

    // Format the first invoice number
    const firstInvoiceNumber = formatInvoiceNumber(
      firstInvoice.invoice_number,
      firstInvoice.issue_date ? new Date(firstInvoice.issue_date) : null
    );

    let isFirstRowInGroup = true;

    // Process each invoice in the group
    for (const invoice of groupInvoices) {
      // Get service name from the billed subject (appointment service or Property Contract)
      const subject = getInvoiceSubject(invoice);
      const itemName = subject.quickBooks.itemName;

      const invoiceServiceDate = formatQuickBooksServiceDate(subject.quickBooks.serviceDate);

      // If invoice has billable line items, create a row for each line item
      const lineItems = quickBooksLineItems(invoice);
      if (lineItems.length > 0) {
        for (let i = 0; i < lineItems.length; i++) {
          const lineItem = lineItems[i];

          // Use first invoice number for ALL rows in the group (including first row)
          const row = [
            firstInvoiceNumber, // *InvoiceNo - use first invoice number for all grouped rows
            isFirstRowInGroup ? propertyName : "", // *Customer - only on first row
            isFirstRowInGroup ? invoiceDate : "", // *InvoiceDate - only on first row
            isFirstRowInGroup ? dueDate : "", // *DueDate - only on first row
            isFirstRowInGroup ? "" : "", // Terms - only on first row
            isFirstRowInGroup ? propertyName : "", // Location - only on first row
            isFirstRowInGroup ? lineItem.description || "" : "", // Memo - only on first row
            lineItem.particular || itemName, // Item(Product/Service)
            unitNumber, // ItemDescription - unit number
            lineItem.quantity?.toString() || "1", // ItemQuantity
            lineItem.unit_price?.toFixed(2) || invoice.amount.toFixed(2), // ItemRate
            lineItem.total_price?.toFixed(2) || invoice.amount.toFixed(2), // *ItemAmount
            invoiceServiceDate, // Service Date - use current invoice's date
          ];
          rows.push(row);

          if (isFirstRowInGroup) {
            isFirstRowInGroup = false;
          }
        }
      } else {
        // No line items, create a single row with invoice total
        const row = [
          firstInvoiceNumber, // *InvoiceNo - use first invoice number for all grouped rows
          isFirstRowInGroup ? propertyName : "", // *Customer - only on first row
          isFirstRowInGroup ? invoiceDate : "", // *InvoiceDate - only on first row
          isFirstRowInGroup ? dueDate : "", // *DueDate - only on first row
          isFirstRowInGroup ? "" : "", // Terms - only on first row
          isFirstRowInGroup ? propertyName : "", // Location - only on first row
          isFirstRowInGroup ? "" : "", // Memo - only on first row
          itemName, // Item(Product/Service)
          unitNumber, // ItemDescription - unit number
          "1", // ItemQuantity
          invoice.amount.toFixed(2), // ItemRate
          invoice.amount.toFixed(2), // *ItemAmount
          invoiceServiceDate, // Service Date - use current invoice's date
        ];
        rows.push(row);

        if (isFirstRowInGroup) {
          isFirstRowInGroup = false;
        }
      }
    }
  }

  // Convert rows to CSV format (comma-separated)
  return rows.map((row) => row.map(escapeCSVField).join(",")).join("\n");
}

export function downloadQuickBooksCSV(invoices: QuickBooksInvoice[], filename?: string): void {
  const csvContent = generateQuickBooksCSV(invoices);

  // Create filename with current date if not provided
  const datePart = new Date().toISOString().split("T")[0];
  const finalFilename = filename || `quickbooks-invoices-${datePart}.csv`;

  // Create blob and download
  const blob = new Blob([csvContent], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = finalFilename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
